// Per-care-task line-art illustrations for the care-task listing card header
// (and reusable on the pillar hero). Same contract as
// `buying-guide-illustrations.tsx`: decorative, monochrome, tinted by the
// consumer via the `color` prop. viewBox 0 0 200 120, anchored bottom-center
// so each subject sits on the shared Baseline.
//
// One illustration per care-task pillar slug: fertilizing, watering, mowing,
// weed-control.

import type { ComponentType } from "react"
import { Frame, Baseline, GroundTufts, type IllustrationProps } from "./buying-guide-illustrations"

// Fertilizing — a broadcast spreader (hopper + wheels + push handle) fanning a
// spray of granules onto the lawn. The granule fan is the detail that keeps it
// from reading like the push mower below.
const Fertilizing: ComponentType<IllustrationProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <Baseline color={color} />
    <GroundTufts color={color} />

    {/* Wheels */}
    <g fill="none" stroke={color} strokeWidth="2.4">
      <circle cx="60" cy="98" r="10" />
      <circle cx="128" cy="98" r="10" />
    </g>
    <g fill={color} stroke="none" opacity="0.85">
      <circle cx="60" cy="98" r="2.6" />
      <circle cx="128" cy="98" r="2.6" />
    </g>

    {/* Hopper — trapezoid that narrows to the spinner disc */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round">
      <path d="M72 52 L116 52 L106 80 L82 80 Z" strokeWidth="2.6" />
      <path d="M69 52 L119 52" strokeWidth="2.2" />
      <path d="M80 80 L108 80" strokeWidth="2" opacity="0.7" />
    </g>

    {/* Push handle up to the right */}
    <g fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M112 54 L150 36" />
      <path d="M108 58 L146 40" />
      <path d="M146 40 L150 36" />
    </g>

    {/* Granules fanning out below the spinner in a tidy downward spread */}
    <g fill={color} stroke="none" opacity="0.8">
      <circle cx="95" cy="89" r="1.4" />
      <circle cx="88" cy="95" r="1.4" />
      <circle cx="102" cy="95" r="1.4" />
      <circle cx="81" cy="102" r="1.4" />
      <circle cx="95" cy="102" r="1.4" />
      <circle cx="109" cy="102" r="1.4" />
      <circle cx="88" cy="108" r="1.3" />
      <circle cx="102" cy="108" r="1.3" />
    </g>
  </Frame>
)

// Watering — a pop-up sprinkler throwing arcs of water to the right, with a few
// falling droplets. Arcs are dashed to read as moving spray.
const Watering: ComponentType<IllustrationProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <Baseline color={color} />
    <GroundTufts color={color} />

    {/* Pop-up sprinkler head rising from a small soil mound. A single grounded
        body reads more clearly as a sprinkler than splayed legs (which looked
        like a figure); the rounded top is the head the water springs from. */}
    <path d="M52 108 C 58 102, 74 102, 80 108" fill="none" stroke={color} strokeWidth="1.6" opacity="0.4" strokeLinecap="round" />
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4">
      <path d="M61 107 L61 95 C 61 91, 63 89, 66 89 C 69 89, 71 91, 71 95 L 71 107" />
      <path d="M60 96 L72 96" strokeWidth="1.8" opacity="0.65" />
    </g>

    {/* Water arcs sweeping right from the head */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeWidth="2" strokeDasharray="2 5" opacity="0.6">
      <path d="M68 88 C 102 54, 142 64, 152 104" />
      <path d="M69 91 C 98 62, 130 70, 142 102" />
      <path d="M66 86 C 84 60, 106 60, 116 92" />
    </g>

    {/* Falling droplets */}
    <g fill={color} stroke="none" opacity="0.8">
      <path d="M120 78 C 122 81, 123 83, 120 84 C 117 83, 118 81, 120 78 Z" />
      <path d="M138 88 C 140 91, 141 93, 138 94 C 135 93, 136 91, 138 88 Z" />
      <path d="M150 98 C 152 101, 153 103, 150 104 C 147 103, 148 101, 150 98 Z" />
    </g>
  </Frame>
)

// Mowing — a walk-behind push mower in side profile facing left, with a few
// taller uncut blades ahead of the deck and clippings behind it.
const Mowing: ComponentType<IllustrationProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <Baseline color={color} />
    <GroundTufts color={color} />

    {/* Taller uncut grass just ahead of the mower */}
    <g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" opacity="0.55">
      <path d="M44 109 C 43 96, 46 92, 45 84" />
      <path d="M50 109 C 51 98, 48 93, 50 86" />
      <path d="M56 109 C 55 99, 58 95, 57 89" />
    </g>

    {/* Deck */}
    <g fill="none" stroke={color} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4">
      <path d="M64 94 L64 87 C 64 84, 66 82, 69 82 L 117 82 C 120 82, 122 84, 122 87 L 122 94 Z" />
    </g>

    {/* Push handle up to the right with a top grip */}
    <g fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M119 86 L 154 52" />
      <path d="M114 88 L 149 54" />
      <path d="M149 52 L 159 52" />
    </g>

    {/* Wheels */}
    <g fill="none" stroke={color} strokeWidth="2.4">
      <circle cx="76" cy="96" r="8" />
      <circle cx="110" cy="96" r="8" />
    </g>
    <g fill={color} stroke="none" opacity="0.85">
      <circle cx="76" cy="96" r="2.4" />
      <circle cx="110" cy="96" r="2.4" />
    </g>

    {/* Clippings flung behind the deck */}
    <g fill={color} stroke="none" opacity="0.6">
      <circle cx="58" cy="90" r="1.3" />
      <circle cx="52" cy="94" r="1.3" />
    </g>
  </Frame>
)

// Weed control — a dandelion (seed-head clock + lobed leaves) with a few seeds
// drifting off, under a light spray mist from the upper right.
const WeedControl: ComponentType<IllustrationProps> = ({ color = "currentColor", className }) => {
  // Seed-head spokes radiating from the flower center.
  const cx = 80
  const cy = 60
  const spokes = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2
    const r1 = 4
    const r2 = 15
    return {
      x1: cx + Math.cos(angle) * r1,
      y1: cy + Math.sin(angle) * r1,
      x2: cx + Math.cos(angle) * r2,
      y2: cy + Math.sin(angle) * r2,
    }
  })

  return (
    <Frame className={className}>
      <Baseline color={color} />
      <GroundTufts color={color} />

      {/* Stem */}
      <path
        d="M80 108 C 78 92, 82 80, 80 66"
        fill="none"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
      />

      {/* Lobed leaves at the base */}
      <g fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M80 104 C 70 106, 62 102, 56 104 C 64 100, 72 100, 80 100" />
        <path d="M80 100 C 88 102, 96 99, 102 101 C 94 96, 86 97, 80 97" />
      </g>

      {/* Dandelion seed-head clock */}
      <g stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.85">
        {spokes.map((s, i) => (
          <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} />
        ))}
      </g>
      <g fill={color} stroke="none" opacity="0.85">
        {spokes.map((s, i) => (
          <circle key={i} cx={s.x2} cy={s.y2} r="1.1" />
        ))}
        <circle cx={cx} cy={cy} r="2" />
      </g>

      {/* Spray fanning from a nozzle tip toward the weed, droplets landing on
          the dandelion — the "control" half of the story. */}
      <g fill="none" stroke={color} strokeLinecap="round" strokeWidth="1.8" strokeDasharray="2 5" opacity="0.55">
        <path d="M160 47 L 110 56" />
        <path d="M161 50 L 114 72" />
        <path d="M162 53 L 120 84" />
      </g>
      <g fill={color} stroke="none" opacity="0.7">
        <circle cx="114" cy="58" r="1.3" />
        <circle cx="118" cy="74" r="1.3" />
        <circle cx="124" cy="84" r="1.3" />
      </g>

      {/* Spray nozzle: a small cone tip with a handle stub in the corner, so
          the dashed fan clearly emits from a sprayer aimed at the weed. */}
      <g fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.85">
        <path d="M159 47 L 170 37 L 174 45 Z" />
        <path d="M172 41 L 180 37" />
      </g>
    </Frame>
  )
}

// Default / fallback — a simple sprout (stem with two leaves) rising from the
// lawn. Rendered for any care-task pillar slug without a bespoke illustration so
// a future pillar never shows an empty card header.
export const DefaultCareTaskIllustration: ComponentType<IllustrationProps> = ({ color = "currentColor", className }) => (
  <Frame className={className}>
    <Baseline color={color} />
    <GroundTufts color={color} />
    <g fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      {/* Stem */}
      <path d="M100 100 L100 50" />
      {/* Left leaf */}
      <path d="M100 74 Q78 70 74 50 Q96 56 100 74 Z" />
      {/* Right leaf */}
      <path d="M100 62 Q122 58 126 38 Q104 44 100 62 Z" />
    </g>
  </Frame>
)

export const CARE_TASK_ILLUSTRATIONS: Record<string, ComponentType<IllustrationProps>> = {
  fertilizing: Fertilizing,
  watering: Watering,
  mowing: Mowing,
  "weed-control": WeedControl,
}
